import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';

import Grid from '@decisiv/ui-components/lib/components/Grid';
import Button from '@decisiv/ui-components/lib/components/Button';
import Link from '@decisiv/ui-components/lib/components/Link';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useSentinelTeam from 'hooks/useSentinelTeam';
import { INVITATION_STATUS } from 'utils/Invitation';
import ROUTE_NAMES from 'setup/Routing/routeNames';
import { buildTeamPath } from 'utils/teamFromUrl';

const LinkButton = styled(Button)`
  display: inherit;
  height: fit-content;
  text-decoration: underline;
  padding: 0;
`;

function labelForStatus(status, linkToCustomer) {
  switch (status) {
    case INVITATION_STATUS.ACCEPTED:
      return <Trans>Accepted Request Sent to {linkToCustomer}</Trans>;
    case INVITATION_STATUS.REJECTED:
      return <Trans>Denied Request Sent to {linkToCustomer}</Trans>;
    default:
      return null;
  }
}

export default function InvitationInfo({
  notificationColor,
  notification,
  source,
  handleOpenModal,
  setModalData,
}) {
  const currentSentinelTeam = useSentinelTeam();
  const customer = source?.invitation?.customer;
  const { sentinelTeam } = notification;

  // Scope the customer link to the team the notification belongs to (the team
  // that requested access), not the team currently being viewed. The route
  // type (customer vs depot) also follows the notification's team type. Without
  // the `/t/:teamKey` prefix the link resolves against the current team, which
  // sends the user to the wrong team or a "Customer not found" error — see
  // PD-3235.
  const customerPath = ROUTE_NAMES.forAssetGrouping(customer, {
    teamType: sentinelTeam?.teamType,
  });
  const to = sentinelTeam?.teamKey
    ? buildTeamPath(sentinelTeam.teamKey, customerPath)
    : customerPath;

  const isDifferentTeam =
    !!sentinelTeam?.id && currentSentinelTeam?.id !== sentinelTeam.id;

  const openChangeTeamModal = () => {
    setModalData(notification);
    handleOpenModal();
  };

  const linkToCustomer = isDifferentTeam ? (
    <LinkButton
      variant="ghost"
      text={source.invitation.customer.name}
      onClick={openChangeTeamModal}
    />
  ) : (
    <Link to={to} text={source.invitation.customer.name} />
  );

  return (
    <Grid.Container padding={0}>
      <Grid.Row>
        <Grid.Column>
          <P as="span" color={notificationColor}>
            {labelForStatus(source?.status, linkToCustomer)}
          </P>
        </Grid.Column>
      </Grid.Row>
    </Grid.Container>
  );
}

InvitationInfo.propTypes = {
  notification: PropTypes.shape({
    sentinelTeam: PropTypes.shape({
      id: PropTypes.string,
      teamKey: PropTypes.string,
      teamType: PropTypes.string,
    }),
  }).isRequired,
  source: PropTypes.shape({
    invitation: PropTypes.shape({
      customer: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    status: PropTypes.string,
  }).isRequired,
  notificationColor: PropTypes.string.isRequired,
  handleOpenModal: PropTypes.func.isRequired,
  setModalData: PropTypes.func.isRequired,
};
